import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { JobService } from '@intranet/modules/jobs/job.service';
import { UserService } from '@intranet/modules/users/user.service';
import {
    JobActionUpdate,
    StatusOption,
} from '@intranet/modules/actions/action.types';
import {
    ActionsToShow,
    Job,
    JobDetailWithActionStatuses,
    JobJiraLink,
    JobMarginUpdate,
    JobStatus,
    JobType,
    JobValue,
    StatusDetail,
} from '../job.types';
import { UserOrGroup } from '@intranet/modules/users/user.types';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActionManageDialog } from '@intranet/modules/actions/dialogs/action-manage/action-manage-dialog.component';
import { Finance, FinanceMargin, FinanceService, TogglService } from '../..';

@Component({
    templateUrl: './jobs-detail.component.html',
    styleUrls: ['./jobs-detail.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class JobsDetailComponent implements OnInit, OnDestroy {
    @ViewChild('actionList') actionListComponent: any;

    isLoading: boolean = false;
    isFinanceLoading: boolean = false;
    finances: Finance[];
    margins: FinanceMargin[];
    showNext: boolean;
    showPrevious: boolean;
    clientJobs: Job[];
    clientJobIndex: number;
    savingChanges: boolean;
    jobOwner: UserOrGroup;
    userOptions: UserOrGroup[];
    jobStatuses: StatusDetail[] = [
        new StatusDetail(JobStatus.Live, 'Live'),
        new StatusDetail(JobStatus.Completed, 'Completed'),
    ];

    allowCompleted: boolean;
    jobTypes: JobType[];
    error: any;
    job: Job;
    jobId: number;
    jobSaveButton: string = 'Save Changes';
    statuses: StatusOption[];
    mostRecentActionUpdate: string;
    actionsToShow: ActionsToShow = ActionsToShow.Live;
    unsubscribe: Subject<void> = new Subject<void>();
    showJobEditDialog = false;
    showMarginEditDialog = false;
    showJiraDialog = false;
    showValueDialog = false;
    jiraLinks: JobJiraLink[];
    jobValue: JobValue;
    linkedJobsValue: JobValue;
    showCopyJobDialog: boolean = false;
    actionJobId: number | null = null;
    actionClientId: number | null = null;
    pinnedJobs: number[] = [];

    constructor(
        public dialog: MatDialog,
        private route: ActivatedRoute,
        private router: Router,
        private jobService: JobService,
        private userService: UserService,
        public  togglService: TogglService,
        private financeService: FinanceService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.route.paramMap
            .pipe(
                takeUntil(this.unsubscribe),
                switchMap((params: ParamMap) => {
                    this.isLoading = true;
                    let id = params.get('jobid');
                    if (!id) {
                        throw new Error('No job id specified');
                    }
                    this.jobId = parseInt(id);
                    return this.jobService.getJobDetails(this.jobId);
                }),
                tap((detail) => {
                    this.processResult(detail);
                    this.isLoading = false;
                })
            )
            .subscribe({
                error: (error) => {
                    this.error = error;
                    this.isLoading = false;
                },
            });

        this.userService.getUserJobsList().subscribe((jobs) => {
            this.pinnedJobs = jobs.map(j => j.jobId);
        });
    }

    refreshDetails() {
        this.isLoading = true;
        this.jobService
            .getJobDetails(this.jobId)
            .subscribe({
                next: (detail) => {
                    this.processResult(detail);
                    this.isLoading = false;
                },
                error: (error) => {
                    this.error = error;
                    this.isLoading = false;
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    processResult(detail: JobDetailWithActionStatuses) {
        this.job = detail.job;
        this.actionJobId = this.job?.jobId;
        this.actionClientId = this.job?.client?.clientId;
        this.statuses = detail.statusOptions;
        this.jobTypes = detail.jobTypes;
        this.clientJobs = detail.clientJobs;
        this.mostRecentActionUpdate = detail.mostRecentActionUpdate;
        this.allowCompleted = detail.allowCompleted;
        this.jiraLinks = detail.jiraLinks;
        this.jobValue = detail.job.marginValueStatus;
        this.linkedJobsValue = detail.linkedJobsValue;

        // Get index of current job in list of all client jobs
        this.clientJobIndex = this.clientJobs.findIndex(
            (j) => j.jobId === this.job.jobId
        );

        if (this.clientJobIndex > -1) {
            this.showPrevious = this.clientJobIndex > 0;
            this.showNext = this.clientJobIndex < this.clientJobs.length - 1;
        }

        this.userOptions = this.userService.sortAndGroupUsersForMenu1(
            this.job.jobOwner
        );

        const owner = this.userOptions.find(
            (uo) => uo.user && uo.user.id === this.job.jobOwnerId
        );

        if (owner) {
            this.jobOwner = owner;
        }

        if (!this.allowCompleted) {
            const completed = this.jobStatuses.find(
                (j) => j.status == JobStatus.Completed
            );
            if (completed) {
                completed.disabled = true;
            }
        }

        const typeForJob = this.jobTypes.find(
            (t) => t.jobTypeId === this.job.jobTypeId
        );
        if (typeForJob) {
            this.job.jobType = typeForJob;
        } else if (this.job.jobType) {
            const divider = new JobType();
            divider.description = 'Inactive Types';
            this.jobTypes.push(divider);
            this.jobTypes.push(this.job.jobType);
        }

        this.finances = [];
        this.isFinanceLoading = true;
        this.financeService
            .getFinanceMarginsForJob(this.job.jobId)
            .pipe(take(1))
            .subscribe((margins) => {
                let finances = margins.finances;
                this.finances = finances;
                this.margins = margins.additionalMargins.margins;
                this.isFinanceLoading = false;
            });
    }

    displayActionManage(
        { action, propertyKey }: JobActionUpdate = {
            action: null,
            propertyKey: null,
        }
    ) {
        const dialogRef = this.dialog.open(ActionManageDialog, {
            width: '80%',
            data: {
                jobActionId: action?.jobActionId,
                jobId: this.jobId,
                propertyKey,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                takeUntil(this.unsubscribe),
                tap((updated) => {
                    if (updated) {
                        this.isLoading = true;
                        this.jobService.getJobDetails(this.jobId).subscribe({
                            next: (detail) => {
                                this.processResult(detail);
                                this.isLoading = false;
                            },
                            error: (error) => (this.error = error),
                        });
                    }
                })
            )
            .subscribe();
    }

    onEditJob() {
        this.showJobEditDialog = true;
    }

    onCopyJob(): void {
        this.showCopyJobDialog = true;
    }

    isJobPinned(jobId: number): boolean {
        return this.pinnedJobs.includes(jobId);
    }

    onPinJob(job: Job): void {
        this.userService.addJobsToUserSelection([job.jobId]).subscribe(() => {
            this.pinnedJobs.push(job.jobId);
        });
    }

    onUnpinJob(job: Job): void {
        this.userService.removeJobsFromUserSelection([job.jobId]).subscribe(() => {
            this.pinnedJobs = this.pinnedJobs.filter(id => id !== job.jobId);
        });
    }

    onStartTimer() {
        this.togglService.startTimerForJob(this.job.jobId).subscribe({
            next: (result) => {
                if (result.success) {
                    //this.refreshDetails();
                } else {
                    this.error = result.error;
                }
            },
            error: (error) => (this.error = error),
        });
    }

    onStopTimer() {
        this.togglService.stopTimer().subscribe({
            next: (result) => {
                if (result.success) {
                    //this.refreshDetails();
                } else {
                    this.error = result.error;
                }
            },
            error: (error) => (this.error = error),
        });
    }

    onCopyJobCancelled() {
        this.showCopyJobDialog = false;
        this.actionJobId = this.job?.jobId;
        this.actionClientId = this.job?.client?.clientId;
        this.cdr.markForCheck();

        // Refresh the action list
        if (this.actionListComponent?.refreshActions) {
            this.actionListComponent.refreshActions();
        }
    }

    onJobCopyChanged(jobId: number): void {
        this.showCopyJobDialog = false;

        if (this.jobId === jobId) {
            if (this.actionListComponent?.refreshActions) {
                this.actionListComponent.refreshActions();
            }
        } else {
            // Navigate to the newly copied job
            this.router.navigateByUrl(`/jobs/detail/${jobId}`);
        }
    }

    onEditMargins() {
        this.showMarginEditDialog = true;
    }

    onLinkedJobsUpdated(updatedValue: JobValue | null) {
        this.linkedJobsValue = updatedValue;
    }

    onSaveMargins(update: JobMarginUpdate)
    {
        this.showMarginEditDialog = false;
        this.isLoading = true;
        this.financeService.updateJobMargins(update).subscribe((result) => {
            this.jobService
            .getJobDetails(this.jobId)
            .pipe(tap((detail) =>
                {
                    this.processResult(detail);
                    this.isLoading = false;
                }))
            .subscribe();
        });
    }

    onJobManageChanged(e: any) {
        this.showJobEditDialog = false;
        this.jobService
            .getJobDetails(this.jobId)
            .pipe(tap((detail) => this.processResult(detail)))
            .subscribe(() => {
                // Editing the job may have toggled its Rock flag, which changes
                // whether its actions pin to the top (and their locked type).
                // Re-fetch the actions so the list updates without a page reload.
                this.actionListComponent?.refreshActions?.();
            });
    }

    onActionSelected(jobActionUpdate: JobActionUpdate) {
        this.displayActionManage(jobActionUpdate);
    }

    onAddAction() {
        this.displayActionManage();
    }

    onAddFinance(financeType: 'estimates' | 'invoices') {
        this.router.navigateByUrl(
            `/finance/${financeType}/create?jobId=${this.job.jobId}`
        );
    }

    goToJob(jobId: number) {
        this.isLoading = true;
        this.router.navigate(['/jobs/detail', jobId]);
    }

    onPreviousJob() {
        if (this.showPrevious) {
            this.goToJob(this.clientJobs[this.clientJobIndex - 1]?.jobId);
        }
    }

    onNextJob() {
        if (this.showNext) {
            this.goToJob(this.clientJobs[this.clientJobIndex + 1]?.jobId);
        }
    }

    onJobChange({ value }: MatSelectChange) {
        this.goToJob(value);
    }

    /** Open the timesheet reports page pre-filtered to this job (and its client).
     *  The reports page reads the `jobs` and `clients` query params into its
     *  Jobs and Clients filters. */
    goToReports(jobId: number, clientId?: number) {
        const queryParams: { jobs: number; clients?: number } = { jobs: jobId };
        if (clientId != null) {
            queryParams.clients = clientId;
        }
        this.router.navigate(['/timesheets/reports'], { queryParams });
    }

    onEditJira() {
        this.showJiraDialog = true;
    }

    onJiraChanged() {
        this.showJiraDialog = false;
        this.refreshDetails();
    }

    onJobValueChanged() {
        this.refreshDetails();
    }
}
